<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div v-if="returnId" class="status-strip" :class="statusClass">
          <div>
            <span class="strip-label">Return No.</span>
            <strong>{{ header.billno || "-" }}</strong>
          </div>
          <span class="status-badge">{{ statusLabel }}</span>
          <p v-if="header.status === 'DRAFT'" class="strip-note">Draft &mdash; stock is not updated until you confirm this return.</p>
          <p v-else-if="isReadonly" class="strip-note">This return is {{ statusLabel.toLowerCase() }} and can no longer be edited.</p>
        </div>

        <!-- Step 1: return type -->
        <section class="sr-card">
          <div class="sr-step-head">
            <span class="sr-step">1</span>
            <div>
              <h4>What kind of return is this?</h4>
              <p>Choose "From a sale bill" whenever the customer has their bill.</p>
            </div>
          </div>
          <div class="type-options" role="radiogroup" aria-label="Sales return type">
            <button
              type="button"
              class="type-option"
              :class="{ active: !isManualMode }"
              role="radio"
              :aria-checked="!isManualMode"
              :disabled="isReadonly"
              @click="setMode(false)"
            >
              <span class="type-radio"></span>
              <span class="type-copy">
                <strong>From a sale bill <em class="type-tag">Recommended</em></strong>
                <small>Find the original bill and pick the items being returned. Rate and tax come from the bill.</small>
              </span>
            </button>
            <button
              type="button"
              class="type-option"
              :class="{ active: isManualMode }"
              role="radio"
              :aria-checked="isManualMode"
              :disabled="isReadonly"
              @click="setMode(true)"
            >
              <span class="type-radio"></span>
              <span class="type-copy">
                <strong>Without a bill</strong>
                <small>Customer has no bill. Pick each item and batch yourself.</small>
              </span>
            </button>
          </div>
        </section>

        <!-- Step 2 (bill mode): find bill and pick items -->
        <section v-if="!isManualMode" class="sr-card">
          <div class="sr-step-head">
            <span class="sr-step">2</span>
            <div>
              <h4>{{ selectedSale ? "Select the items being returned" : "Find the original sale bill" }}</h4>
              <p>{{ selectedSale ? "Tick each item the customer is returning, then enter the quantity and reason." : "Search by bill number, customer name, or mobile number." }}</p>
            </div>
          </div>

          <template v-if="!selectedSale">
            <div class="lookup-bar">
              <i class="fa fa-search" aria-hidden="true"></i>
              <input
                ref="saleSearchInput"
                v-model="saleSearch"
                type="search"
                class="form-ctrl lookup-input"
                placeholder="e.g. INV-1024, Ravi, 98765..."
                :disabled="isReadonly"
                @input="searchSales"
              />
            </div>

            <div v-if="saleLoading || saleDetailLoading" class="empty-panel">
              {{ saleDetailLoading ? "Loading bill..." : "Searching sales..." }}
            </div>
            <div v-else-if="saleSearch && availableSales.length" class="sale-results">
              <button
                v-for="sale in availableSales"
                :key="sale.saleid"
                type="button"
                class="sale-result"
                :disabled="isReadonly"
                @click="selectSale(sale.saleid)"
              >
                <span class="sale-result-main">
                  <strong>{{ sale.billno || sale.saleid }}</strong>
                  <small>{{ formatDate(sale.billdate) }} &middot; {{ sale.customername || "Walk-in" }}{{ sale.mobile ? ` · ${sale.mobile}` : "" }}</small>
                </span>
                <span class="sale-result-side">
                  <strong>Rs. {{ formatNumber(sale.netamount) }}</strong>
                  <small>{{ formatQty(sale.totalqty) }} qty</small>
                </span>
                <span class="sale-result-cta">Select &rsaquo;</span>
              </button>
            </div>
            <div v-else-if="saleSearch.trim()" class="empty-panel">No bills match "{{ saleSearch }}".</div>
            <div v-else class="empty-panel">Start typing to find the bill.</div>
          </template>

          <template v-else>
            <div class="bill-card">
              <div class="bill-card-meta">
                <div><span>Bill No.</span><strong>{{ selectedSale.billno || selectedSale.saleid }}</strong></div>
                <div v-if="selectedSale.billdate"><span>Bill Date</span><strong>{{ formatDate(selectedSale.billdate) }}</strong></div>
                <div><span>Customer</span><strong>{{ selectedSale.customername || "Walk-in" }}</strong></div>
                <div v-if="selectedSale.mobile"><span>Mobile</span><strong>{{ selectedSale.mobile }}</strong></div>
              </div>
              <button v-if="!isReadonly" type="button" class="link-btn" @click="changeBill">Change bill</button>
            </div>

            <div v-if="saleDetailLoading" class="empty-panel">Loading bill items...</div>
            <div v-else-if="!billRows.length" class="empty-panel">
              {{ isReadonly ? "No items on this return." : "This bill has no items available to return." }}
            </div>
            <div v-else class="table-responsive">
              <table class="sr-table">
                <thead>
                  <tr>
                    <th v-if="!isReadonly" class="col-check"></th>
                    <th>Item</th>
                    <th class="num">Sold</th>
                    <th class="num">Returned before</th>
                    <th class="col-qty">Return Qty</th>
                    <th class="num">Rate</th>
                    <th class="col-cond">Condition</th>
                    <th class="col-reason">Reason</th>
                    <th class="num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="entry in billRows"
                    :key="entry.key"
                    :class="{ 'is-picked': entry.line, 'is-unavailable': !entry.line && entry.canReturn <= 0 }"
                  >
                    <td v-if="!isReadonly" class="col-check">
                      <input
                        type="checkbox"
                        :checked="!!entry.line"
                        :disabled="!entry.line && entry.canReturn <= 0"
                        :aria-label="`Return ${entry.itemname}`"
                        @change="toggleLinkedRow(entry.row, $event.target.checked)"
                      />
                    </td>
                    <td>
                      <div class="item-name">{{ entry.itemname }}</div>
                      <div class="item-sub">
                        Batch {{ entry.batchno || "-" }}
                        <template v-if="entry.expirydate"> &middot; Exp {{ formatDate(entry.expirydate) }}</template>
                        <span v-if="isExpired(entry.expirydate)" class="warn-chip">Expired</span>
                      </div>
                    </td>
                    <td class="num">{{ entry.soldqty === null ? "-" : formatQty(entry.soldqty) }}</td>
                    <td class="num">{{ entry.returnedqty === null ? "-" : formatQty(entry.returnedqty) }}</td>
                    <td class="col-qty">
                      <div v-if="entry.line" class="qty-field">
                        <input
                          v-model.number="entry.line.returnqty"
                          type="number"
                          min="0"
                          :max="entry.line.maxreturnableqty === null ? undefined : entry.line.maxreturnableqty"
                          class="table-input qty-input"
                          :disabled="isReadonly"
                          @input="recalculateLine(entry.line)"
                        />
                        <small v-if="entry.line.maxreturnableqty !== null && !isReadonly">of {{ formatQty(entry.line.maxreturnableqty) }}</small>
                      </div>
                      <span v-else-if="entry.canReturn > 0" class="muted">Up to {{ formatQty(entry.canReturn) }}</span>
                      <span v-else class="muted">Fully returned</span>
                    </td>
                    <td class="num">
                      Rs. {{ formatNumber(entry.rate) }}
                      <div v-if="entry.istaxinclusive || entry.taxpercent" class="item-sub">
                        {{ formatQty(entry.taxpercent) }}% {{ entry.taxtype || "GST" }}{{ entry.istaxinclusive ? " incl." : "" }}
                      </div>
                    </td>
                    <td class="col-cond">
                      <div v-if="entry.line" class="cond-toggle" role="group" aria-label="Item condition">
                        <button type="button" :class="{ on: entry.line.restockable }" :disabled="isReadonly" @click="setCondition(entry.line, true)">Good</button>
                        <button type="button" class="bad" :class="{ on: !entry.line.restockable }" :disabled="isReadonly" @click="setCondition(entry.line, false)">Damaged</button>
                      </div>
                    </td>
                    <td class="col-reason">
                      <input
                        v-if="entry.line"
                        v-model="entry.line.reason"
                        list="return-reasons"
                        class="table-input"
                        :class="{ 'is-missing': showErrors && !String(entry.line.reason || '').trim() }"
                        placeholder="Why returned?"
                        :disabled="isReadonly"
                      />
                    </td>
                    <td class="num strong">{{ entry.line ? `Rs. ${formatNumber(lineTotal(entry.line))}` : "" }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </section>

        <!-- Step 2 (manual mode): item lines -->
        <section v-else class="sr-card">
          <div class="sr-step-head">
            <span class="sr-step">2</span>
            <div>
              <h4>Add the items being returned</h4>
              <p>Pick the item, then the batch it came from. Rate and tax fill in from the batch.</p>
            </div>
          </div>

          <div v-if="!lines.length" class="empty-panel">No items yet.</div>
          <div v-else class="table-responsive">
            <table class="sr-table">
              <thead>
                <tr>
                  <th class="col-idx">#</th>
                  <th class="col-item">Item</th>
                  <th class="col-batch">Batch</th>
                  <th class="col-qty">Qty</th>
                  <th class="col-rate">Rate</th>
                  <th class="col-tax">Tax %</th>
                  <th class="col-cond">Condition</th>
                  <th class="col-reason">Reason</th>
                  <th class="num">Amount</th>
                  <th v-if="!isReadonly"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(line, index) in lines" :key="line.key">
                  <td class="col-idx muted">{{ index + 1 }}</td>
                  <td class="col-item">
                    <select
                      v-model="line.itemid"
                      class="table-input"
                      :class="{ 'is-missing': showErrors && !line.itemid }"
                      :disabled="isReadonly"
                      @change="onManualItemChange(line)"
                    >
                      <option value="">Select item</option>
                      <option v-for="item in itemOptions" :key="item.itemid" :value="item.itemid">{{ item.itemname }}</option>
                    </select>
                  </td>
                  <td class="col-batch">
                    <select
                      v-model="line.purchaseitemid"
                      class="table-input"
                      :class="{ 'is-missing': showErrors && line.itemid && !line.purchaseitemid }"
                      :disabled="isReadonly || !line.itemid"
                      @change="onManualBatchChange(line)"
                    >
                      <option value="">{{ line.itemid ? "Select batch" : "Pick item first" }}</option>
                      <option v-for="batch in line.batchOptions" :key="batch.purchaseitemid" :value="batch.purchaseitemid">
                        {{ batch.batchno || batch.billno || batch.purchaseid }} (stock {{ formatQty(batch.remainingqty) }})
                      </option>
                    </select>
                    <div v-if="line.expirydate" class="item-sub">
                      Exp {{ formatDate(line.expirydate) }}
                      <span v-if="isExpired(line.expirydate)" class="warn-chip">Expired</span>
                    </div>
                  </td>
                  <td class="col-qty">
                    <input v-model.number="line.returnqty" type="number" min="0" class="table-input qty-input" :disabled="isReadonly" @input="recalculateLine(line)" />
                  </td>
                  <td class="col-rate">
                    <input v-model.number="line.rate" type="number" min="0" class="table-input" :disabled="isReadonly" @input="recalculateLine(line)" />
                    <div v-if="line.istaxinclusive" class="item-sub">incl. tax</div>
                  </td>
                  <td class="col-tax">
                    <input v-model.number="line.taxpercent" type="number" min="0" class="table-input" :disabled="isReadonly" @input="recalculateLine(line)" />
                  </td>
                  <td class="col-cond">
                    <div class="cond-toggle" role="group" aria-label="Item condition">
                      <button type="button" :class="{ on: line.restockable }" :disabled="isReadonly" @click="setCondition(line, true)">Good</button>
                      <button type="button" class="bad" :class="{ on: !line.restockable }" :disabled="isReadonly" @click="setCondition(line, false)">Damaged</button>
                    </div>
                  </td>
                  <td class="col-reason">
                    <input
                      v-model="line.reason"
                      list="return-reasons"
                      class="table-input"
                      :class="{ 'is-missing': showErrors && !String(line.reason || '').trim() }"
                      placeholder="Why returned?"
                      :disabled="isReadonly"
                    />
                  </td>
                  <td class="num strong">Rs. {{ formatNumber(lineTotal(line)) }}</td>
                  <td v-if="!isReadonly" class="text-end">
                    <button type="button" class="icon-btn" title="Remove item" aria-label="Remove item" @click="removeLine(index)">&times;</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <button v-if="!isReadonly" type="button" class="add-line-btn" @click="addManualLine">+ Add another item</button>
        </section>

        <datalist id="return-reasons">
          <option v-for="reason in reasonSuggestions" :key="reason" :value="reason" />
        </datalist>

        <div v-if="expiredRestockableLines.length" class="warning-box">
          <strong>Check expired items:</strong> {{ expiredRestockableLines.length }} expired item(s) are marked "Good" and will go back into stock.
          Mark them "Damaged" if they should not be resold.
        </div>

        <!-- Step 3: details + summary -->
        <div class="final-grid">
          <section class="sr-card">
            <div class="sr-step-head">
              <span class="sr-step">3</span>
              <div>
                <h4>Return details</h4>
                <p>Date, customer and any notes for this return.</p>
              </div>
            </div>
            <div class="details-grid">
              <div class="form-group">
                <label class="form-label">Return Date</label>
                <b-form-input v-model="header.returndate" type="date" class="form-ctrl" :disabled="isReadonly" />
              </div>
              <div class="form-group">
                <label class="form-label">Customer</label>
                <select
                  v-if="isManualMode"
                  v-model="header.customerid"
                  class="form-ctrl form-select"
                  :disabled="isReadonly"
                  @change="applyManualCustomer"
                >
                  <option value="">Walk-in / No customer</option>
                  <option v-for="customer in customers" :key="customer.customerid" :value="customer.customerid">
                    {{ customer.customername }}{{ customer.mobile ? ` (${customer.mobile})` : "" }}
                  </option>
                </select>
                <b-form-input v-else :value="linkedCustomerLabel" readonly class="form-ctrl bg-light" />
              </div>
              <div class="form-group details-full">
                <label class="form-label">Remarks <span class="muted">(optional)</span></label>
                <b-form-textarea v-model="header.remarks" rows="2" class="form-ctrl" :disabled="isReadonly" />
              </div>
            </div>
          </section>

          <section class="sr-card summary-card">
            <h4 class="summary-title">Refund Summary</h4>
            <div class="summary-row muted-row">
              <span>Items</span>
              <span>{{ lines.length }} item(s), {{ formatQty(summaryBreakdown.qty) }} qty</span>
            </div>
            <div class="summary-row">
              <span>Taxable value</span>
              <span>Rs. {{ formatNumber(summaryBreakdown.taxable) }}</span>
            </div>
            <div class="summary-row">
              <span>Tax <small class="muted">(CGST {{ formatNumber(summaryBreakdown.cgst) }} + SGST {{ formatNumber(summaryBreakdown.sgst) }}<template v-if="summaryBreakdown.igst"> + IGST {{ formatNumber(summaryBreakdown.igst) }}</template>)</small></span>
              <span>Rs. {{ formatNumber(summary.taxamount) }}</span>
            </div>
            <div class="summary-row input-row">
              <label for="sr-discount">Discount</label>
              <input id="sr-discount" v-model.number="header.discountamount" type="number" min="0" class="table-input summary-input" :disabled="isReadonly" />
            </div>
            <div class="summary-row input-row">
              <label for="sr-roundoff">Round off</label>
              <input id="sr-roundoff" v-model.number="header.roundoff" type="number" class="table-input summary-input" :disabled="isReadonly" />
            </div>
            <div class="summary-net">
              <span>Net Refund</span>
              <strong>Rs. {{ formatNumber(summary.netamount) }}</strong>
            </div>
          </section>
        </div>

        <div class="footer-actions">
          <router-link to="/salesreturnlist" class="btn btn-cancel-light">Back to list</router-link>
          <div class="footer-right">
            <button v-if="returnId" class="btn btn-outline-secondary" :disabled="saving" @click="printNote()">
              <i class="fa fa-print me-1" aria-hidden="true"></i> {{ header.status === "DRAFT" ? "Preview" : "Print" }}
            </button>
            <button
              v-if="returnId && ['DRAFT', 'CONFIRMED'].includes(header.status)"
              class="btn btn-outline-danger"
              :disabled="saving"
              @click="cancelReturn()"
            >
              Cancel Return
            </button>
            <template v-if="!isReadonly">
              <button class="btn btn-outline-secondary" :disabled="saving" @click="saveDraft()">
                {{ saving ? "Saving..." : returnId ? "Save Changes" : "Save as Draft" }}
              </button>
              <button class="btn btn-submit" :disabled="saving" @click="saveAndConfirm()">
                {{ saving ? "Please wait..." : "Confirm Return" }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { openPdfPreview } from "@/assets/utils/pdfPreview";
import { fetchSalesReturnPdf } from "@/services/invoicePdf";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const createManualLine = () => ({
  key: `manual-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  salesitemid: null,
  purchaseitemid: "",
  itemid: "",
  itemname: "",
  batchno: "",
  expirydate: "",
  returnqty: 1,
  maxreturnableqty: null,
  rate: 0,
  costprice: 0,
  taxtype: "GST",
  taxpercent: 0,
  taxablevalue: 0,
  cgst: 0,
  sgst: 0,
  igst: 0,
  amount: 0,
  taxamount: 0,
  discount: 0,
  istaxinclusive: false,
  restockable: true,
  reason: "",
  batchOptions: [],
});

export default {
  name: "createsalesreturn",
  data() {
    return {
      manualMode: false,
      customers: [],
      itemOptions: [],
      availableSales: [],
      selectedSale: null,
      selectedSaleItems: [],
      saleSearch: "",
      saleLoading: false,
      saleDetailLoading: false,
      loading: false,
      saving: false,
      saleSearchTimer: null,
      showErrors: false,
      reasonSuggestions: ["Damaged", "Expired", "Defective", "Wrong item", "Customer changed mind", "Excess quantity"],
      lines: [],
      header: {
        billno: "",
        originalsaleid: null,
        customerid: "",
        customername: "",
        mobile: "",
        returndate: this.getToday(),
        locationid: this.$store.getters.getCurrentLocation?.locationid || "",
        status: "NEW",
        discountamount: 0,
        roundoff: 0,
        remarks: "",
      },
    };
  },
  computed: {
    returnId() {
      return this.$route.query.id || "";
    },
    title() {
      return this.returnId ? "Edit Sales Return" : "Create Sales Return";
    },
    title1() {
      return this.returnId ? "Review or update this sales return" : "Record items a customer is bringing back";
    },
    statusLabel() {
      const labels = { DRAFT: "Draft", CONFIRMED: "Confirmed", CANCELLED: "Cancelled" };
      return labels[this.header.status] || this.header.status || "";
    },
    billRows() {
      const rows = this.selectedSaleItems.map((row) => {
        const line = this.findLinkedLine(row);
        return {
          key: row.key,
          row,
          line,
          itemname: row.itemname,
          batchno: row.batchno,
          expirydate: row.expirydate,
          soldqty: Number(row.soldqty || 0),
          returnedqty: Number(row.confirmedreturnedqty || 0),
          canReturn: Number(row.maxreturnableqty || 0),
          rate: line ? line.rate : Number(row.rate || 0),
          taxpercent: Number(row.taxpercent || 0),
          taxtype: row.taxtype,
          istaxinclusive: this.normalizeInclusiveFlag(row.istaxinclusive, Number(row.rate || 0) > 0),
        };
      });
      // Lines saved on this return whose bill row is no longer listed still need to be shown.
      const orphans = this.lines
        .filter((line) => !this.selectedSaleItems.some((row) => this.isSameLinkedRow(line, row)))
        .map((line) => ({
          key: line.key,
          row: line,
          line,
          itemname: line.itemname,
          batchno: line.batchno,
          expirydate: line.expirydate,
          soldqty: null,
          returnedqty: null,
          canReturn: 0,
          rate: line.rate,
          taxpercent: line.taxpercent,
          taxtype: line.taxtype,
          istaxinclusive: line.istaxinclusive,
        }));
      const all = [...rows, ...orphans];
      return this.isReadonly ? all.filter((entry) => entry.line) : all;
    },
    summaryBreakdown() {
      const sum = (field) => Number(this.lines.reduce((total, line) => total + Number(line[field] || 0), 0).toFixed(2));
      return {
        qty: sum("returnqty"),
        taxable: sum("taxablevalue"),
        cgst: sum("cgst"),
        sgst: sum("sgst"),
        igst: sum("igst"),
      };
    },
    isReadonly() {
      return Boolean(this.returnId) && this.header.status !== "DRAFT";
    },
    isManualMode() {
      return this.manualMode;
    },
    linkedCustomerLabel() {
      if (this.header.customername || this.header.mobile) {
        const mobile = this.header.mobile ? ` (${this.header.mobile})` : "";
        return `${this.header.customername || "Walk-in customer"}${mobile}`;
      }
      return "Customer will fill after selecting a sale bill";
    },
    statusClass() {
      return {
        "badge-draft": this.header.status === "DRAFT",
        "badge-confirmed": this.header.status === "CONFIRMED",
        "badge-cancelled": this.header.status === "CANCELLED",
      };
    },
    summary() {
      const totalamount = this.lines.reduce((sum, line) => sum + Number(line.amount || 0), 0);
      const taxamount = this.lines.reduce((sum, line) => sum + Number(line.taxamount || 0), 0);
      const discountamount = Number(this.header.discountamount || 0);
      const roundoff = Number(this.header.roundoff || 0);
      const baseNet = this.lines.reduce((sum, line) => sum + Number(this.lineTotal(line) || 0), 0);
      return {
        totalamount: Number(totalamount.toFixed(2)),
        taxamount: Number(taxamount.toFixed(2)),
        netamount: Number((baseNet - discountamount + roundoff).toFixed(2)),
      };
    },
    expiredRestockableLines() {
      return this.lines.filter((line) => line.restockable && this.isExpired(line.expirydate));
    },
    locationOptions() {
      return (this.$store.getters.getLocations || []).map((location) => ({
        value: location.locationid,
        text: location.locationname,
      }));
    },
  },
  async beforeMount() {
    await Promise.all([this.fetchCustomers(), this.fetchItems()]);
    if (this.returnId) {
      await this.loadReturn();
    } else if (this.isManualMode) {
      this.addManualLine();
    }
  },
  beforeDestroy() {
    clearTimeout(this.saleSearchTimer);
  },
  watch: {
    "header.locationid": {
      async handler(newValue, oldValue) {
        if (!newValue || newValue === oldValue || this.isReadonly) return;
        this.availableSales = [];
        this.selectedSale = null;
        this.selectedSaleItems = [];
        if (!this.isManualMode) {
          this.lines = [];
          if (this.saleSearch.trim()) {
            this.searchSales();
          }
          return;
        }
        await Promise.all(
          this.lines
            .filter((line) => line.itemid)
            .map((line) => this.onManualItemChange(line))
        );
      },
    },
  },
  methods: {
    getToday() {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    },
    getCurrentUserId() {
      const attrs = this.$store?.getters?.getUserDetails;
      if (!Array.isArray(attrs)) return null;
      const sub = attrs.find((attr) => attr.Name === "sub");
      return sub?.Value || null;
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatQty(value) {
      return Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
    },
    normalizeInclusiveFlag(value, fallback = false) {
      if (value === true || value === 1 || value === "1" || value === "true") return true;
      if (value === false || value === 0 || value === "0" || value === "false") return false;
      return fallback;
    },
    lineTotal(line) {
      const amount = Number(line.amount || 0);
      const tax = Number(line.taxamount || 0);
      return line.istaxinclusive ? amount : (amount + tax);
    },
    isExpired(value) {
      if (!value) return false;
      return new Date(value) < new Date(`${this.getToday()}T00:00:00`);
    },
    async fetchCustomers() {
      const response = await axios.get("/v1/customers");
      this.customers = response.data || [];
    },
    async fetchItems() {
      const response = await axios.get("/v1/sales/items");
      this.itemOptions = Array.isArray(response.data) ? response.data : response.data.items || [];
    },
    applyManualCustomer() {
      const customer = this.customers.find((row) => row.customerid === this.header.customerid);
      this.header.customername = customer?.customername || "";
      this.header.mobile = customer?.mobile || "";
    },
    async setMode(isManual) {
      if (this.isReadonly) return;
      if (this.manualMode === isManual) return;
      const hasWork = this.lines.some((line) => line.itemid) || Boolean(this.selectedSale);
      if (hasWork) {
        const result = await Swal.fire({
          icon: "question",
          title: "Switch return type?",
          text: "The items you have added so far will be cleared.",
          showCancelButton: true,
          confirmButtonText: "Yes, switch",
        });
        if (!result.isConfirmed) return;
      }
      this.showErrors = false;
      this.saleSearch = "";
      this.availableSales = [];
      this.manualMode = isManual;
      this.lines = [];
      if (!this.manualMode) {
        this.header.originalsaleid = null;
        this.header.customerid = "";
        this.header.customername = "";
        this.header.mobile = "";
        this.selectedSale = null;
        this.selectedSaleItems = [];
      } else {
        this.header.originalsaleid = null;
        this.header.customerid = "";
        this.header.customername = "";
        this.header.mobile = "";
        this.addManualLine();
      }
    },
    searchSales() {
      clearTimeout(this.saleSearchTimer);
      this.saleSearchTimer = setTimeout(async () => {
        if (!this.saleSearch.trim()) {
          this.availableSales = [];
          return;
        }
        this.saleLoading = true;
        try {
          const response = await axios.get("/v1/sales-returns/sale-search", {
            params: {
              search: this.saleSearch.trim(),
              locationid: this.header.locationid || this.$store.getters.getCurrentLocation?.locationid || undefined,
            },
          });
          this.availableSales = (response.data || []).map((row) => ({ ...row, key: row.saleid }));
        } catch (error) {
          Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to search sales." });
        } finally {
          this.saleLoading = false;
        }
      }, 300);
    },
    async selectSale(saleid) {
      this.saleDetailLoading = true;
      try {
        const response = await axios.get(`/v1/sales-returns/sales/${saleid}`, {
          params: {
            locationid: this.header.locationid || this.$store.getters.getCurrentLocation?.locationid || undefined,
          },
        });
        this.manualMode = false;
        this.selectedSale = response.data;
        this.selectedSaleItems = (response.data.items || []).map((row) => ({
          ...row,
          key: `${row.salesitemid}-${row.purchaseitemid || "none"}`,
        }));
        this.header.originalsaleid = response.data.saleid;
        this.header.customerid = response.data.customerid || "";
        this.header.customername = response.data.customername || "";
        this.header.mobile = response.data.mobile || "";
        this.lines = [];
        this.saleSearch = "";
        this.availableSales = [];
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to load sale detail." });
      } finally {
        this.saleDetailLoading = false;
      }
    },
    isSameLinkedRow(line, row) {
      return Number(line.salesitemid) === Number(row.salesitemid) && Number(line.purchaseitemid) === Number(row.purchaseitemid);
    },
    findLinkedLine(row) {
      return this.lines.find((line) => this.isSameLinkedRow(line, row)) || null;
    },
    hasLinkedLine(row) {
      return Boolean(this.findLinkedLine(row));
    },
    toggleLinkedRow(row, checked) {
      if (this.isReadonly) return;
      if (checked) {
        this.addLinkedLine(row);
      } else {
        this.lines = this.lines.filter((line) => !this.isSameLinkedRow(line, row));
      }
    },
    async changeBill() {
      if (this.lines.length) {
        const result = await Swal.fire({
          icon: "question",
          title: "Choose a different bill?",
          text: "The items you picked from this bill will be cleared.",
          showCancelButton: true,
          confirmButtonText: "Yes, change bill",
        });
        if (!result.isConfirmed) return;
      }
      this.selectedSale = null;
      this.selectedSaleItems = [];
      this.lines = [];
      this.header.originalsaleid = null;
      this.header.customerid = "";
      this.header.customername = "";
      this.header.mobile = "";
      this.$nextTick(() => this.$refs.saleSearchInput?.focus());
    },
    setCondition(line, restockable) {
      if (this.isReadonly || line.restockable === restockable) return;
      line.restockable = restockable;
      this.handleRestockableToggle(line);
    },
    addLinkedLine(row) {
      if (this.hasLinkedLine(row)) return;
      const line = {
        key: `linked-${row.salesitemid}-${row.purchaseitemid}`,
        salesitemid: row.salesitemid,
        purchaseitemid: row.purchaseitemid,
        itemid: row.itemid,
        itemname: row.itemname,
        batchno: row.batchno,
        expirydate: row.expirydate,
        returnqty: row.maxreturnableqty > 0 ? 1 : 0,
        maxreturnableqty: Number(row.maxreturnableqty || 0),
        rate: Number(row.rate || 0),
        costprice: Number(row.costprice || 0),
        taxtype: row.taxtype || "GST",
        taxpercent: Number(row.taxpercent || 0),
        taxablevalue: Number(row.taxablevalue || 0),
        cgst: Number(row.cgst || 0),
        sgst: Number(row.sgst || 0),
        igst: Number(row.igst || 0),
        amount: 0,
        taxamount: 0,
        discount: Number(row.discount || 0),
        istaxinclusive: this.normalizeInclusiveFlag(row.istaxinclusive, Number(row.rate || 0) > 0),
        restockable: true,
        reason: "",
        batchOptions: [],
      };
      this.recalculateLine(line);
      this.lines.push(line);
    },
    addManualLine() {
      this.lines.push(createManualLine());
    },
    async onManualItemChange(line) {
      const selectedItem = this.itemOptions.find((row) => row.itemid === line.itemid);
      line.itemname = selectedItem?.itemname || "";
      line.purchaseitemid = "";
      line.batchno = "";
      line.expirydate = "";
      line.batchOptions = [];
      if (!line.itemid) return;
      const response = await axios.get(`/v1/sales-returns/manual-batches/${line.itemid}`, {
        params: {
          locationid: this.header.locationid || this.$store.getters.getCurrentLocation?.locationid || undefined,
        },
      });
      line.batchOptions = response.data || [];
    },
    onManualBatchChange(line) {
      const batch = line.batchOptions.find((row) => Number(row.purchaseitemid) === Number(line.purchaseitemid));
      if (!batch) return;
      line.itemid = batch.itemid;
      line.itemname = batch.itemname;
      line.batchno = batch.batchno;
      line.expirydate = batch.expirydate;
      line.rate = Number(batch.mrp || 0);
      line.costprice = Number(batch.purchaseprice || 0);
      line.taxtype = batch.taxtype || "GST";
      line.taxpercent = Number(batch.taxpercent || 0);
      line.taxablevalue = Number(batch.taxablevalue || 0);
      line.cgst = Number(batch.cgst || 0);
      line.sgst = Number(batch.sgst || 0);
      line.igst = Number(batch.igst || 0);
      line.istaxinclusive = this.normalizeInclusiveFlag(batch.istaxinclusive, Number(batch.mrp || 0) > 0);
      this.recalculateLine(line);
    },
    recalculateLine(line) {
      if (line.maxreturnableqty !== null && Number(line.returnqty || 0) > Number(line.maxreturnableqty || 0)) {
        line.returnqty = Number(line.maxreturnableqty || 0);
      }
      const qty = Number(line.returnqty || 0);
      const rate = Number(line.rate || 0);
      const taxpercent = Number(line.taxpercent || 0);
      const gross = Number((qty * rate).toFixed(2));
      if (line.istaxinclusive && taxpercent > 0) {
        line.taxablevalue = Number((gross * 100 / (100 + taxpercent)).toFixed(2));
        line.amount = gross;
        line.taxamount = Number((gross - line.taxablevalue).toFixed(2));
      } else {
        line.taxablevalue = gross;
        line.amount = gross;
        line.taxamount = Number((line.taxablevalue * taxpercent / 100).toFixed(2));
      }
      line.cgst = Number((line.taxamount / 2).toFixed(2));
      line.sgst = Number((line.taxamount / 2).toFixed(2));
      line.igst = 0;
    },
    handleRestockableToggle(line) {
      if (line.restockable && this.isExpired(line.expirydate)) {
        Swal.fire({
          icon: "warning",
          title: "Expired batch warning",
          text: "This batch is expired. Are you sure it is restockable?",
        });
      }
    },
    removeLine(index) {
      this.lines.splice(index, 1);
    },
    buildPayload() {
      return {
        originalsaleid: this.isManualMode ? null : this.header.originalsaleid,
        locationid: this.header.locationid || null,
        customerid: this.header.customerid || null,
        customername: this.header.customername || null,
        mobile: this.header.mobile || null,
        returndate: this.header.returndate,
        discountamount: Number(this.header.discountamount || 0),
        taxamount: this.summary.taxamount,
        roundoff: Number(this.header.roundoff || 0),
        netamount: this.summary.netamount,
        remarks: this.header.remarks || null,
        createdby: this.getCurrentUserId(),
        items: this.lines.map((line) => ({
          salesitemid: line.salesitemid || null,
          purchaseitemid: Number(line.purchaseitemid),
          itemid: line.itemid,
          returnqty: Number(line.returnqty || 0),
          rate: Number(line.rate || 0),
          costprice: Number(line.costprice || 0),
          taxtype: line.taxtype || null,
          taxpercent: Number(line.taxpercent || 0),
          taxablevalue: Number(line.taxablevalue || 0),
          cgst: Number(line.cgst || 0),
          sgst: Number(line.sgst || 0),
          igst: Number(line.igst || 0),
          taxamount: Number(line.taxamount || 0),
          discount: Number(line.discount || 0),
          amount: Number(line.amount || 0),
          istaxinclusive: Boolean(line.istaxinclusive),
          batchno: line.batchno || null,
          expirydate: line.expirydate || null,
          restockable: Boolean(line.restockable),
          reason: line.reason || "",
        })),
      };
    },
    validateDraft() {
      const fail = (text) => {
        this.showErrors = true;
        Swal.fire({ icon: "warning", title: "Almost there", text });
        return false;
      };
      if (!this.isManualMode && !this.header.originalsaleid) {
        return fail("Find and select the original sale bill first.");
      }
      if (!this.lines.length) {
        return fail(this.isManualMode ? "Add at least one item being returned." : "Tick at least one item from the bill to return.");
      }
      for (let index = 0; index < this.lines.length; index += 1) {
        const line = this.lines[index];
        const name = line.itemname || `row ${index + 1}`;
        if (!line.itemid) {
          return fail(`Select an item for row ${index + 1}.`);
        }
        if (!line.purchaseitemid) {
          return fail(`Select a batch for ${name}.`);
        }
        if (!(Number(line.returnqty) > 0)) {
          return fail(`Enter a return quantity for ${name}.`);
        }
        if (line.maxreturnableqty !== null && Number(line.returnqty) > Number(line.maxreturnableqty || 0)) {
          return fail(`Only ${this.formatQty(line.maxreturnableqty)} of ${name} can be returned.`);
        }
        if (!line.reason || !line.reason.trim()) {
          return fail(`Enter a reason for returning ${name}.`);
        }
      }
      this.showErrors = false;
      return true;
    },
    async confirmExpiredRestockable() {
      if (!this.expiredRestockableLines.length) return true;
      const result = await Swal.fire({
        icon: "warning",
        title: "Expired items marked Good",
        text: "Some expired items will go back into sellable stock. Do you want to continue?",
        showCancelButton: true,
        confirmButtonText: "Continue",
      });
      return result.isConfirmed;
    },
    async saveDraft({ silent = false, skipExpiredCheck = false } = {}) {
      if (!this.validateDraft()) return false;
      if (!skipExpiredCheck && !(await this.confirmExpiredRestockable())) return false;
      this.saving = true;
      try {
        const payload = this.buildPayload();
        if (this.returnId) {
          await axios.put(`/v1/sales-returns/${this.returnId}`, payload);
          if (!silent) Swal.fire({ icon: "success", title: "Changes saved", timer: 1400, showConfirmButton: false });
        } else {
          const response = await axios.post("/v1/sales-returns", payload);
          const newId = response.data?.[0];
          if (!silent) Swal.fire({ icon: "success", title: "Draft saved", timer: 1400, showConfirmButton: false });
          if (newId) {
            await this.$router.replace({ path: "/createsalesreturn", query: { id: newId } });
            await this.loadReturn();
          }
        }
        return true;
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to save sales return draft." });
        return false;
      } finally {
        this.saving = false;
      }
    },
    async saveAndConfirm() {
      if (!this.validateDraft()) return;
      if (!(await this.confirmExpiredRestockable())) return;
      const result = await Swal.fire({
        title: "Confirm this return?",
        html: `Refund <strong>Rs. ${this.formatNumber(this.summary.netamount)}</strong> for ${this.lines.length} item(s).<br><small>Good items go back into stock. This cannot be edited afterwards.</small>`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, confirm",
      });
      if (!result.isConfirmed) return;
      const saved = await this.saveDraft({ silent: true, skipExpiredCheck: true });
      if (!saved || !this.returnId) return;
      await this.confirmReturn({ skipPrompts: true });
    },
    async loadReturn() {
      this.loading = true;
      try {
        const response = await axios.get(`/v1/sales-returns/${this.returnId}`);
        const detail = response.data;
        this.header = {
          billno: detail.billno,
          originalsaleid: detail.originalsaleid,
          customerid: detail.customerid || "",
          customername: detail.customername || "",
          mobile: detail.mobile || "",
          returndate: detail.returndate || this.getToday(),
          locationid: detail.locationid || this.$store.getters.getCurrentLocation?.locationid || "",
          status: detail.status,
          discountamount: Number(detail.discountamount || 0),
          roundoff: Number(detail.roundoff || 0),
          remarks: detail.remarks || "",
        };

        if (detail.originalsaleid) {
          this.manualMode = false;
          await this.selectSale(detail.originalsaleid);
        } else {
          this.manualMode = true;
        }

        const nextLines = [];
        for (const item of detail.items || []) {
          const line = {
            key: `loaded-${item.id}`,
            salesitemid: item.salesitemid,
            purchaseitemid: item.purchaseitemid,
            itemid: item.itemid,
            itemname: item.itemname,
            batchno: item.batchno,
            expirydate: item.expirydate,
            returnqty: Number(item.returnqty || 0),
            maxreturnableqty: null,
            rate: Number(item.rate || 0),
            costprice: Number(item.costprice || 0),
            taxtype: item.taxtype || "GST",
            taxpercent: Number(item.taxpercent || 0),
            taxablevalue: Number(item.taxablevalue || 0),
            cgst: Number(item.cgst || 0),
            sgst: Number(item.sgst || 0),
            igst: Number(item.igst || 0),
            amount: Number(item.amount || 0),
            taxamount: Number(item.taxamount || 0),
            discount: Number(item.discount || 0),
            istaxinclusive: this.normalizeInclusiveFlag(item.istaxinclusive, Number(item.rate || 0) > 0),
            restockable: Boolean(item.restockable),
            reason: item.reason || "",
            batchOptions: [],
          };
          if (item.salesitemid && this.selectedSaleItems.length) {
            const source = this.selectedSaleItems.find(
              (row) => Number(row.salesitemid) === Number(item.salesitemid) && Number(row.purchaseitemid) === Number(item.purchaseitemid)
            );
            line.maxreturnableqty = source ? Number(source.maxreturnableqty || 0) + Number(item.returnqty || 0) : Number(item.returnqty || 0);
          } else if (!item.salesitemid && item.itemid) {
            const batchResponse = await axios.get(`/v1/sales-returns/manual-batches/${item.itemid}`, {
              params: {
                locationid: detail.locationid || this.$store.getters.getCurrentLocation?.locationid || undefined,
              },
            });
            line.batchOptions = batchResponse.data || [];
          }
          nextLines.push(line);
        }
        this.lines = nextLines.length ? nextLines : (this.isManualMode ? [createManualLine()] : []);
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to load sales return." });
      } finally {
        this.loading = false;
      }
    },
    async confirmReturn({ skipPrompts = false } = {}) {
      if (!skipPrompts) {
        if (!(await this.confirmExpiredRestockable())) return;
        const result = await Swal.fire({
          title: "Confirm sales return?",
          text: "This will post stock and damaged-goods movements in one transaction.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, confirm",
        });
        if (!result.isConfirmed) return;
      }
      this.saving = true;
      try {
        await axios.post(`/v1/sales-returns/${this.returnId}/confirm`, { actorid: this.getCurrentUserId() });
        Swal.fire({ icon: "success", title: "Return confirmed", timer: 1400, showConfirmButton: false });
        await this.loadReturn();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to confirm sales return." });
      } finally {
        this.saving = false;
      }
    },
    async printNote() {
      if (!this.returnId) return;
      Swal.fire({ title: "Preparing credit note...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      try {
        const { blob, fileName } = await fetchSalesReturnPdf(this.returnId);
        Swal.close();
        openPdfPreview(blob, { title: `Credit Note ${this.header.billno || ""}`.trim(), fileName });
      } catch (error) {
        Swal.fire({ icon: "error", title: "Print Error", text: "Could not load the credit note PDF." });
      }
    },
    async cancelReturn() {
      const result = await Swal.fire({
        title: "Cancel sales return?",
        text: this.header.status === "CONFIRMED"
          ? "This will write reversal ledger entries and may be blocked if returned stock was consumed again."
          : "This draft return will be cancelled.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, cancel it",
      });
      if (!result.isConfirmed) return;
      this.saving = true;
      try {
        await axios.post(`/v1/sales-returns/${this.returnId}/cancel`, { actorid: this.getCurrentUserId() });
        Swal.fire({ icon: "success", title: "Return cancelled", timer: 1400, showConfirmButton: false });
        await this.loadReturn();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to cancel sales return." });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.sr-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 16px;
}
.sr-step-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}
.sr-step {
  flex: 0 0 28px;
  height: 28px;
  border-radius: 50%;
  background: #fff4e8;
  color: #c2410c;
  font-weight: 700;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sr-step-head h4 {
  margin: 2px 0 2px;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}
.sr-step-head p {
  margin: 0;
  font-size: 0.82rem;
  color: #6b7280;
}

/* status strip */
.status-strip {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 12px 18px;
  margin-bottom: 16px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
}
.strip-label {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
}
.status-strip strong {
  font-size: 1rem;
  color: #111827;
}
.status-badge {
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  background: #e5e7eb;
  color: #374151;
}
.strip-note {
  margin: 0 0 0 auto;
  font-size: 0.82rem;
  color: #6b7280;
}
.badge-draft { background: #fffaf3; border-color: #fed7aa; }
.badge-draft .status-badge { background: #ffedd5; color: #c2410c; }
.badge-confirmed { background: #f6fef9; border-color: #bbf7d0; }
.badge-confirmed .status-badge { background: #dcfce7; color: #15803d; }
.badge-cancelled { background: #fef8f8; border-color: #fecaca; }
.badge-cancelled .status-badge { background: #fee2e2; color: #dc2626; }

/* step 1 */
.type-options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.type-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  padding: 14px 16px;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.type-option:hover:not(:disabled) { border-color: #fdba74; }
.type-option:disabled { cursor: not-allowed; opacity: 0.7; }
.type-option.active {
  border-color: #FF9F43;
  background: #fffaf5;
}
.type-radio {
  flex: 0 0 18px;
  height: 18px;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  position: relative;
}
.type-option.active .type-radio { border-color: #FF9F43; }
.type-option.active .type-radio::after {
  content: "";
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  background: #FF9F43;
}
.type-copy { display: flex; flex-direction: column; gap: 4px; }
.type-copy strong { color: #111827; font-size: 0.92rem; }
.type-copy small { color: #6b7280; font-size: 0.8rem; line-height: 1.4; }
.type-tag {
  font-style: normal;
  font-size: 0.68rem;
  font-weight: 600;
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #dcfce7;
  color: #15803d;
  vertical-align: middle;
}

/* bill lookup */
.lookup-bar {
  position: relative;
  max-width: 480px;
  margin-bottom: 12px;
}
.lookup-bar i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}
.lookup-input {
  width: 100%;
  padding-left: 34px;
}
.sale-results {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  max-height: 340px;
  overflow-y: auto;
}
.sale-result {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #f1f2f4;
  background: #fff;
  text-align: left;
}
.sale-result:last-child { border-bottom: 0; }
.sale-result:hover:not(:disabled) { background: #fffaf5; }
.sale-result-main { flex: 1; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.sale-result-main strong { color: #111827; font-size: 0.9rem; }
.sale-result-main small, .sale-result-side small { color: #6b7280; font-size: 0.78rem; }
.sale-result-side { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
.sale-result-side strong { color: #111827; font-size: 0.88rem; }
.sale-result-cta { color: #c2410c; font-weight: 600; font-size: 0.82rem; white-space: nowrap; }

.bill-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 16px;
  margin-bottom: 14px;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  background: #fffaf5;
}
.bill-card-meta { display: flex; gap: 28px; flex-wrap: wrap; }
.bill-card-meta span {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
}
.bill-card-meta strong { font-size: 0.9rem; color: #111827; }
.link-btn {
  border: 1px solid #fdba74;
  background: #fff;
  color: #c2410c;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 0.8rem;
  font-weight: 600;
}
.link-btn:hover { background: #fff4e8; }

/* tables */
.sr-table {
  width: 100%;
  border-collapse: collapse;
}
.sr-table th,
.sr-table td {
  padding: 10px 10px;
  border-bottom: 1px solid #f1f2f4;
  font-size: 0.85rem;
  vertical-align: middle;
}
.sr-table th {
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.sr-table tbody tr.is-picked td { background: #fffaf5; }
.sr-table tbody tr.is-unavailable td { color: #9ca3af; }
.sr-table .num { text-align: right; white-space: nowrap; }
.sr-table .strong { font-weight: 600; color: #111827; }
.col-check { width: 36px; }
.col-check input { width: 17px; height: 17px; accent-color: #FF9F43; cursor: pointer; }
.col-idx { width: 32px; }
.col-item { min-width: 190px; }
.col-batch { min-width: 170px; }
.col-qty { width: 120px; }
.col-rate { width: 110px; }
.col-tax { width: 80px; }
.col-cond { width: 150px; }
.col-reason { min-width: 170px; }
.item-name { font-weight: 500; color: #111827; }
.item-sub { font-size: 0.74rem; color: #6b7280; margin-top: 2px; }
.muted { color: #9ca3af; font-size: 0.8rem; }
.table-input {
  width: 100%;
  min-height: 34px;
  padding: 4px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  font-size: 0.85rem;
  background: #fff;
}
.table-input:focus { outline: none; border-color: #FF9F43; box-shadow: 0 0 0 2px rgba(255, 159, 67, 0.15); }
.table-input:disabled { background: #f9fafb; }
.table-input.is-missing { border-color: #f87171; background: #fef2f2; }
.qty-field { display: flex; align-items: center; gap: 6px; }
.qty-field small { color: #9ca3af; font-size: 0.75rem; white-space: nowrap; }
.qty-input { width: 72px; }

.cond-toggle {
  display: inline-flex;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  overflow: hidden;
}
.cond-toggle button {
  border: 0;
  background: #fff;
  padding: 5px 10px;
  font-size: 0.76rem;
  font-weight: 600;
  color: #6b7280;
}
.cond-toggle button + button { border-left: 1px solid #e5e7eb; }
.cond-toggle button.on { background: #dcfce7; color: #15803d; }
.cond-toggle button.bad.on { background: #fee2e2; color: #dc2626; }
.cond-toggle button:disabled { cursor: default; }

.icon-btn {
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #9ca3af;
  font-size: 1.2rem;
  line-height: 1;
}
.icon-btn:hover { background: #fef2f2; color: #dc2626; }
.add-line-btn {
  margin-top: 12px;
  border: 1px dashed #fdba74;
  background: #fffaf5;
  color: #c2410c;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 0.84rem;
  font-weight: 600;
}
.add-line-btn:hover { background: #fff4e8; }

.warn-chip {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #fef3c7;
  color: #b45309;
  font-size: 0.68rem;
  font-weight: 700;
}
.empty-panel {
  padding: 18px;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.85rem;
  text-align: center;
}
.warning-box {
  padding: 12px 16px;
  margin-bottom: 16px;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  background: #fffbeb;
  color: #92400e;
  font-size: 0.84rem;
}

/* step 3 */
.final-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 16px;
  align-items: start;
}
.details-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}
.details-full { grid-column: 1 / -1; }
.form-label {
  display: block;
  margin-bottom: 6px;
  color: #374151;
  font-size: 0.82rem;
  font-weight: 600;
}
.form-ctrl {
  min-height: 38px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.summary-title {
  margin: 0 0 10px;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}
.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  font-size: 0.86rem;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;
}
.muted-row { color: #6b7280; font-size: 0.8rem; }
.input-row label { margin: 0; }
.summary-input { width: 120px; text-align: right; }
.summary-net {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fff4e8;
  color: #c2410c;
}
.summary-net span { font-weight: 600; }
.summary-net strong { font-size: 1.3rem; }

/* footer */
.footer-actions {
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 16px;
  margin-top: 4px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 -4px 14px rgba(15, 23, 42, 0.06);
}
.footer-right { display: flex; gap: 10px; flex-wrap: wrap; }
.btn-cancel-light {
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
}
.footer-actions .btn { min-height: 40px; padding: 8px 18px; font-weight: 600; }

@media (max-width: 1100px) {
  .final-grid { grid-template-columns: 1fr; }
}
@media (max-width: 768px) {
  .type-options,
  .details-grid { grid-template-columns: 1fr; }
  .sr-card { padding: 14px; }
  .strip-note { margin-left: 0; flex-basis: 100%; }
  .footer-actions, .footer-right { justify-content: stretch; }
  .footer-right .btn, .footer-actions > .btn { flex: 1; }
}
</style>
